import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { pointerIconPng, pointerIconXsPng } from '../../assets/images';
import { YouTubeVideo } from '../../components/YouTubeVideo';
import { userAtom } from '../../jotai-atoms/user.atom';
import { User } from '../../models/User';
import { ProfileService } from '../../services/profile';
import { Button } from '../../stories/Button';
import { Header } from '../../stories/Header';
import { VideoModal } from './VideoModal';

enum P2L_ONBOARDING_YOUTUBE_VIDEO_ID {
  LATAM = `hfhmAHuTNmE`,
  BR = `q7PehbCCngQ`,
}

export const Play2LearnOnboardingPage: React.FC = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [isVideoWatched, setIsVideoWatched] = useState<boolean>(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [user, setUser] = useAtom(userAtom);

  const __ = useTranslation().t;

  const buttonLabel = __('returnToMainPage');
  const youtubeVideoId = useMemo(() => {
    if (user?.lang === 'pt-BR') {
      return P2L_ONBOARDING_YOUTUBE_VIDEO_ID.BR;
    }

    return P2L_ONBOARDING_YOUTUBE_VIDEO_ID.LATAM;
  }, [user?.lang]);

  const onVideoEnd = () => {
    setIsVideoWatched(true);
  };

  useEffect(() => {
    if (!isVideoWatched) {
      return;
    }

    ProfileService.postEnablePlay2LearnClasses()
      .then((response) => {
        if (response.data.status === 'success') {
          setIsButtonEnabled(true);
          setUser({
            ...user,
            isGameClassKahootBeta: false,
          } as User);
        } else {
          alert(__('whopsSomethingWentWrong'));
        }
      })
      .catch(() => {
        alert(__('whopsSomethingWentWrong'));
      });
  }, [isVideoWatched]);

  const onButtonClick = () => {
    window.location.href = `/`;
  };

  const CTAButton = useCallback(
    ({ className }: { className: string }) => (
      <Button
        label={buttonLabel}
        isPulsing={isButtonEnabled}
        disabled={!isButtonEnabled}
        className={className}
        onClick={onButtonClick}
      />
    ),
    [isButtonEnabled, buttonLabel],
  );

  return (
    <>
      <Header type="logo-only" />
      <Background>
        <MainDiv>
          <Container>
            <div className="d-none d-lg-block">
              <Trans alt={__('welcomeToPlay2learn')} i18nKey="welcomeToPlay2learn">
                <StyledH1 color="blue">Welcome to Play2learn</StyledH1>
                <StyledH1 color="orange">Now more than ever, have fun learning!</StyledH1>
              </Trans>
            </div>
            <MainDescription className="d-none d-lg-block">
              {__('experienceNewLiveClassesMessage')}
            </MainDescription>
            <CTAButton className="d-none d-lg-inline-block" />
            <YouTubeContainer>
              <StyledMessage>
                <Trans
                  alt={__('watchVideoToLearnAboutNewClasses')}
                  i18nKey="watchVideoToLearnAboutNewClasses"
                >
                  <StyledSpan color="orange">Watch the video</StyledSpan>{' '}
                  <StyledSpan color="blue">to learn about these new classes</StyledSpan>
                </Trans>
              </StyledMessage>
              <StyledPointerImg src={pointerIconPng} alt="Pointer" className="d-none d-lg-block" />
              <StyledPointerImg
                src={pointerIconXsPng}
                alt="Pointer"
                className="d-block d-lg-none"
              />
              <MobileEventCatcher
                onClick={() => setIsVideoModalOpen(true)}
                className="d-block d-lg-none"
              />
              <EmbedContainer className="embed-container">
                <YouTubeVideo
                  autoplayEnabled={false}
                  videoId={youtubeVideoId}
                  onEnd={() => onVideoEnd()}
                />
              </EmbedContainer>
            </YouTubeContainer>
            <CTAButton className="d-inline-block d-lg-none" />
          </Container>
        </MainDiv>
        <FeaturesDiv>
          <Container>
            <StyledH2>{__('whyTakeThisClass')}</StyledH2>
            <FeaturesGrid>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#0083FF' }}>1</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('itsFun')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('kahootTransformClassroom')}
                  </div>
                </div>
              </FeatureCard>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#43CC79' }}>2</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('boostLearning')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('gamesBoostLearning')}
                  </div>
                </div>
              </FeatureCard>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#ffdf00' }}>3</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('gamesImproveFocus')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('timedActivitiesMotivation')}
                  </div>
                </div>
              </FeatureCard>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#FF6600' }}>4</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('takeRisks')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('kahootBuildConfidence')}
                  </div>
                </div>
              </FeatureCard>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#FF1053' }}>5</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('newActivityTypes')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('gameClassesIncludeNewActivities')}
                  </div>
                </div>
              </FeatureCard>
              <FeatureCard>
                <FeatureCardNumber style={{ color: '#B21C88' }}>6</FeatureCardNumber>
                <div>
                  <FeatureCardTitle>{__('noMicRequired')}</FeatureCardTitle>
                  <div className="gameclass-features-card-description">
                    {__('gamepadInteractionToSubmitResponses')}
                  </div>
                </div>
              </FeatureCard>
            </FeaturesGrid>
          </Container>
        </FeaturesDiv>
        <Footer>Copyright © {new Date().getFullYear()} Open Education LLC.</Footer>
      </Background>
      <VideoModal
        show={isVideoModalOpen}
        setShow={setIsVideoModalOpen}
        setEnded={() => onVideoEnd()}
        youtubeVideoId={youtubeVideoId}
      />
    </>
  );
};

const Background = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
`;

const MainDiv = styled.div`
  padding-top: 64px;
  padding-bottom: 257px;
  background-color: ${({ theme }) => theme.colors.backgroundSuperLight};
  text-align: center;
  position: relative;
  z-index: 1;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    padding-top: 112px;
    padding-bottom: 32px;
  }
`;

const MainDescription = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black};
  max-width: 637px;
  margin: 12px auto 24px;
`;

interface StyledH1Props {
  color: 'blue' | 'orange';
}

const StyledH1 = styled.h1<StyledH1Props>`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  color: ${({ theme, color }) => (color === 'blue' ? theme.colors.blue : theme.colors.orange)};
`;

const StyledH2 = styled.h2`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 32px;
  line-height: 32px;
  text-align: center;
  color: ${({ theme }) => theme.colors.blue};
  margin-bottom: 60px !important;
`;

const FeaturesDiv = styled.div`
  position: relative;
  padding-top: 267px;
  padding-bottom: 40px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    padding-top: 64px;
    padding-bottom: 0;
  }
`;

const FeaturesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    display: block;
  }
`;

const FeatureCard = styled.div`
  flex: 0 0 33.333333%;
  padding: 0 36px 40px;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black};

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    padding: 0 0 40px;
    display: flex;
  }
`;

const FeatureCardNumber = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 40px;
  line-height: 64px;
  width: 64px;
  height: 64px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 50%;
  text-align: center;
  flex-shrink: 0;
  margin-right: 24px;
`;

const FeatureCardTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.bold.fontFamily};
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 4px;
  margin-top: 24px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    margin-top: 0;
  }
`;

const Footer = styled.div`
  padding: 26px 0;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  text-align: center;
`;

const YouTubeContainer = styled.div`
  width: 719px;
  height: 363px;
  border-radius: 12px;
  position: absolute;
  transform: translate(-50%, 60px);
  left: 50%;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    width: 100%;
    height: auto;
    position: relative;
    transform: none;
    left: 0;
    margin-bottom: 32px;
  }
`;

const StyledMessage = styled.div`
  position: absolute;
  left: -50%;
  transform: translateX(50%);
  top: -105px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    right: 14px;
    left: initial;
    top: -87px;
    transform: none;
    max-width: 260px;
  }

  @media screen and (min-width: ${({ theme }) => theme.screenSizes.lg}) and (max-width: ${({
      theme,
    }) => theme.screenSizes.xl}) {
    max-width: 280px;
    top: -110px;
    left: -38%;
  }
`;

interface StyledSpanProps {
  color: 'blue' | 'orange';
}

const StyledSpan = styled.span<StyledSpanProps>`
  font-family: 'Gochi Hand', cursive;
  font-size: 24px;
  line-height: 24px;
  color: ${({ theme, color }) => (color === 'blue' ? theme.colors.blue : theme.colors.orange)};
`;

const StyledPointerImg = styled.img`
  position: absolute;
  width: 71px;
  height: 92px;
  left: -83px;
  top: -49px;

  @media screen and (max-width: ${({ theme }) => theme.screenSizes.lg}) {
    width: 46px;
    height: 57px;
    right: 14px;
    left: initial;
    top: -67px;
  }
`;

const EmbedContainer = styled.div`
  border-radius: 12px;
`;

const MobileEventCatcher = styled.div`
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;
